/**
 * SectionRoute
 *
 * Protects routes by section access:
 * - Redirects to /login if not authenticated or token expired
 * - Redirects to /403 if section not allowed (agents only)
 * - Super admins always pass
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  isAuthenticated,
  clearAuthAndRedirectToLogin,
  isSuperAdmin,
} from "../utils/auth";
import { getCurrentUser } from "../apis/auth";
import { hasSectionAccess } from "../utils/sectionAccess";

function readCachedAuth() {
  let sections = [];
  let permissions = [];
  try {
    const s = localStorage.getItem("sections");
    sections = s ? JSON.parse(s) : [];
  } catch {
    sections = [];
  }
  try {
    const p = localStorage.getItem("agentPermissions");
    permissions = p ? JSON.parse(p) : [];
  } catch {
    permissions = [];
  }
  return { sections, permissions };
}

const SectionRoute = ({ children, section }) => {
  const navigate = useNavigate();
  const [sectionsReady, setSectionsReady] = useState(!section || isSuperAdmin());

  useEffect(() => {
    if (!isAuthenticated()) {
      clearAuthAndRedirectToLogin();
      return;
    }

    if (!section) return;

    if (isSuperAdmin()) {
      setSectionsReady(true);
      return;
    }

    let cancelled = false;
    const cached = readCachedAuth();

    const checkAccess = (sections, permissions) => {
      if (cancelled) return;
      if (!hasSectionAccess(section, sections, permissions)) {
        navigate("/403");
      } else {
        setSectionsReady(true);
      }
    };

    if (hasSectionAccess(section, cached.sections, cached.permissions)) {
      setSectionsReady(true);
    }

    getCurrentUser()
      .then((res) => {
        const freshSections = res?.data?.sections;
        const freshPerms = res?.data?.permissions;
        const sections = Array.isArray(freshSections) ? freshSections : cached.sections;
        const permissions = Array.isArray(freshPerms) ? freshPerms : cached.permissions;
        if (Array.isArray(freshSections)) {
          localStorage.setItem("sections", JSON.stringify(freshSections));
        }
        if (Array.isArray(freshPerms)) {
          localStorage.setItem("agentPermissions", JSON.stringify(freshPerms));
        }
        checkAccess(sections, permissions);
      })
      .catch(() => {
        if (!hasSectionAccess(section, cached.sections, cached.permissions)) {
          navigate("/403");
        } else {
          setSectionsReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [navigate, section]);

  if (!isAuthenticated()) return null;

  if (section && !sectionsReady) return null;

  return children;
};

export default SectionRoute;
